import React, { useState, useEffect } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { fetchCommunities, submitInvoice } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import InputField from "../../utils/InputField"; // Import the InputField component

function InvoiceForm() {
  const [communities, setCommunities] = useState([]);
  const [formData, setFormData] = useState({
    community: null,
    servicesRendered: "",
    startDate: "",
    endDate: "",
    billingDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0],
    totalAmount: "",
  });
  const [errors, setErrors] = useState({});
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadCommunities = async () => {
      try {
        const data = await fetchCommunities(1, 100);
        setCommunities(data?.data);
      } catch (error) {
        showToast("Failed to load communities", "error");
      }
    };
    loadCommunities();
  }, []);

  const handleCommunityChange = (value) => {
    const selectedCommunity = communities.find(
      (community) => community.communityName === value
    );
    setFormData((prevData) => ({
      ...prevData,
      community: selectedCommunity,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast(
        "Please correct the errors in the form before submitting.",
        "error"
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await submitInvoice(formData);
      showToast("Invoice submitted successfully!", "success");
      setFormData({
        communityId: "",
        servicesRendered: "",
        startDate: "",
        endDate: "",
        billingDate: "",
        dueDate: "",
        totalAmount: "",
      });
    } catch (error) {
      showToast("Failed to submit invoice. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md="6">
          <div className="form-heading mb-4 text-uppercase fw-bold">
            Invoice Information
          </div>

          <InputField
            field="community"
            label="Select a Community"
            value={formData.community?.communityName || ""}
            onChange={(field, value) => handleCommunityChange(value)}
            error={errors.community}
            type="select"
            options={communities.map((community) => community.communityName)}
          />
        </Col>

        <Col md="6">
          <Row className="mb-3">
            <Col md="12">
              <InputField
                field="servicesRendered"
                label="Services Rendered"
                value={formData.servicesRendered}
                onChange={handleChange}
                error={errors.servicesRendered}
                type="select"
                options={["Simplepermit ™ License"]}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="6">
              <InputField
                field="startDate"
                label="Start Date"
                value={formData.startDate}
                onChange={handleChange}
                error={errors.startDate}
                type="date"
              />
            </Col>
            <Col md="6">
              <InputField
                field="endDate"
                label="End Date"
                value={formData.endDate}
                onChange={handleChange}
                error={errors.endDate}
                type="date"
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md="6">
              <InputField
                field="billingDate"
                label="Billing Date"
                value={formData.billingDate}
                onChange={handleChange}
                error={errors.billingDate}
                type="date"
              />
            </Col>
            <Col md="6">
              <InputField
                field="dueDate"
                label="Due Date"
                value={formData.dueDate}
                onChange={handleChange}
                error={errors.dueDate}
                type="date"
              />
            </Col>
          </Row>

          {/* Total Amount */}
          <InputField
            field="totalAmount"
            label="Total Amount"
            value={formData.totalAmount}
            onChange={handleChange}
            error={errors.totalAmount}
            type="number"
            min={0}
          />
        </Col>
      </Row>

      {/* Submit Button */}
      <div className="d-flex mb-4 justify-content-end mt-4">
        <Button
          type="submit"
          variant="secondary"
          size="xl"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Send Invoice"}
        </Button>
      </div>
    </Form>
  );
}

export default InvoiceForm;
